/**
 * useAgents — load trading agents for the desk (read path).
 * Merges REST agents with optional realtime status. Never invents stub agents.
 */

'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AITradingAgent } from '@/infrastructure/api'
import type { RealtimeAgentStatus } from '@/infrastructure/websocket/hooks/useRealtimeAITrading'
import {
  toPresentationStatus,
  type AgentPresentationStatus,
  type AgentRawStatus,
} from '../lib/agent-status'

export interface AgentListItem {
  id: string
  name: string
  mandate: string | null
  strategy: string
  modelId: string | null
  /** Raw lifecycle status for control actions */
  status: AITradingAgent['status']
  presentationStatus: AgentPresentationStatus
  riskLevel: string
  maxPositionSize: number | null
  maxDailyLoss: number | null
  venueAffinity: string | null
  symbols: string[]
  pnl: number | null
  winRate: number | null
  openPositions: number | null
  tradesCount: number | null
  lastActivity: string | null
  /** True when fields were enriched from the live stream */
  live: boolean
  /** Underlying REST agent when available */
  source: AITradingAgent | null
  /** Portal paper agent — never live P&L */
  paper: boolean
  lastPaperOrderId?: string
  lastPaperOrderStatus?: string
}

export interface AgentStatusCounts {
  running: number
  paused: number
  error: number
  waiting: number
  total: number
}

export interface UseAgentsReturn {
  agents: AgentListItem[]
  /** Raw REST agents (for mutations / configure). */
  rawAgents: AITradingAgent[]
  counts: AgentStatusCounts
  isLoading: boolean
  error: string | null
  /** Live QTP unreachable — paper desk may still be shown. */
  liveUnavailable: boolean
  refresh: () => Promise<void>
  upsertAgent: (agent: AITradingAgent) => void
  /** Apply a realtime status snapshot (optional stream). */
  applyRealtimeStatuses: (statuses: RealtimeAgentStatus[]) => void
}

function mapRisk(level: string | undefined): string {
  if (!level) return '—'
  return level
}

/** Only finite numbers count as metrics — missing/NaN never become stub zeros. */
function finiteOrNull(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

function restToListItem(agent: AITradingAgent, live?: RealtimeAgentStatus): AgentListItem {
  // REST is system-of-record for lifecycle. Live may enrich metrics, not invent a seat.
  const controlStatus: AITradingAgent['status'] =
    agent.status === 'active' ||
      agent.status === 'paused' ||
      agent.status === 'stopped' ||
      agent.status === 'error'
      ? agent.status
      : 'stopped'
  const liveLifecycle: AgentRawStatus | null =
    live?.status === 'active' ||
      live?.status === 'paused' ||
      live?.status === 'error' ||
      live?.status === 'stopped'
      ? live.status
      : null
  const presentationStatus = toPresentationStatus(liveLifecycle ?? controlStatus)

  return {
    id: agent.id,
    name: agent.name,
    mandate:
      typeof agent.mandate === 'string' && agent.mandate.trim()
        ? agent.mandate.trim()
        : null,
    strategy: agent.strategy || '—',
    modelId:
      typeof agent.model_id === 'string' && agent.model_id.trim()
        ? agent.model_id.trim()
        : null,
    status: liveLifecycle
      ? liveLifecycle === 'active'
        ? 'active'
        : liveLifecycle === 'paused'
          ? 'paused'
          : liveLifecycle === 'error'
            ? 'error'
            : 'stopped'
      : controlStatus,
    presentationStatus,
    riskLevel: mapRisk(live?.riskLevel ?? agent.risk_level),
    maxPositionSize: finiteOrNull(agent.max_position_size),
    maxDailyLoss: finiteOrNull(agent.max_daily_loss),
    venueAffinity:
      typeof agent.broker === 'string' && agent.broker.trim()
        ? agent.broker.trim()
        : null,
    symbols: agent.allowed_symbols ?? [],
    // Only finite numbers count as live metrics — never default missing to 0
    pnl: finiteOrNull(live?.pnl) ?? finiteOrNull(agent.performance?.pnl),
    winRate: finiteOrNull(live?.winRate) ?? finiteOrNull(agent.performance?.win_rate),
    openPositions: finiteOrNull(live?.openPositions),
    tradesCount:
      finiteOrNull(live?.tradesCount) ?? finiteOrNull(agent.performance?.total_trades),
    lastActivity: live?.lastActivity ?? agent.updated_at ?? null,
    live: Boolean(live),
    source: agent,
    paper: agent.broker === 'paper' || agent.model_id === 'paper',
    lastPaperOrderId: agent.last_paper_order_id,
    lastPaperOrderStatus: agent.last_paper_order_status,
  }
}

type PaperAgentRow = {
  id: string
  name: string
  mandate?: string
  status: 'active' | 'paused' | 'stopped' | string
  mode?: string
  paperFills?: number
  updatedAt?: string
  lastPaperOrder?: { orderId?: string; status?: string }
}

function paperToRestAgent(p: PaperAgentRow): AITradingAgent {
  const status: AITradingAgent['status'] =
    p.status === 'active' ? 'active' : p.status === 'paused' ? 'paused' : 'stopped'
  return {
    id: p.id,
    name: p.name,
    model_id: 'paper',
    mandate: p.mandate?.trim() || undefined,
    strategy: 'Paper rehearsal',
    status,
    risk_level: 'conservative',
    max_position_size: 0,
    max_daily_loss: 0,
    allowed_symbols: [],
    broker: 'paper',
    performance: {
      total_trades: typeof p.paperFills === 'number' ? p.paperFills : 0,
      win_rate: Number.NaN,
      pnl: Number.NaN,
      sharpe_ratio: Number.NaN,
      max_drawdown: Number.NaN,
    },
    created_at: p.updatedAt || new Date().toISOString(),
    updated_at: p.updatedAt || new Date().toISOString(),
    last_paper_order_id: p.lastPaperOrder?.orderId,
    last_paper_order_status: p.lastPaperOrder?.status,
  }
}

let paperCache: AITradingAgent[] = []
const paperListeners = new Set<(agents: AITradingAgent[]) => void>()

function publishPaper(agents: AITradingAgent[]) {
  paperCache = agents
  paperListeners.forEach((fn) => fn(agents))
}

function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const id = window.setTimeout(() => resolve(fallback), ms)
    promise.then(
      (value) => {
        window.clearTimeout(id)
        resolve(value)
      },
      () => {
        window.clearTimeout(id)
        resolve(fallback)
      }
    )
  })
}

async function fetchPaperAgents(): Promise<{ ok: boolean; agents: AITradingAgent[] }> {
  try {
    const res = await withTimeout(
      fetch('/api/paper-agents', { credentials: 'include', cache: 'no-store' }),
      4000,
      null
    )
    if (!res || !res.ok) return { ok: paperCache.length > 0, agents: paperCache }
    const json = (await res.json()) as { agents?: PaperAgentRow[] }
    const agents = (json.agents ?? []).map(paperToRestAgent)
    if (agents.length > 0) publishPaper(agents)
    return { ok: true, agents }
  } catch {
    return { ok: paperCache.length > 0, agents: paperCache }
  }
}

function unwrapAgentList(json: unknown): AITradingAgent[] {
  if (Array.isArray(json)) return json as AITradingAgent[]
  if (json && typeof json === 'object') {
    const rec = json as { data?: unknown; agents?: unknown }
    if (Array.isArray(rec.data)) return rec.data as AITradingAgent[]
    if (Array.isArray(rec.agents)) return rec.agents as AITradingAgent[]
  }
  return []
}

async function fetchLiveAgents(): Promise<{ ok: boolean; agents: AITradingAgent[]; message?: string }> {
  try {
    const res = await withTimeout(
      fetch('/api/v1/ai-trading/agents', { credentials: 'include', cache: 'no-store' }),
      4000,
      null
    )
    if (!res) return { ok: false, agents: [], message: 'Live agent list timed out' }
    const json = await res.json().catch(() => null)
    if (!res.ok) {
      return {
        ok: false,
        agents: [],
        message: (json as { message?: string } | null)?.message || `Live agent list ${res.status}`,
      }
    }
    return { ok: true, agents: unwrapAgentList(json) }
  } catch {
    return { ok: false, agents: [], message: 'Live agent list timed out' }
  }
}

function countStatuses(items: AgentListItem[]): AgentStatusCounts {
  const counts: AgentStatusCounts = {
    running: 0,
    paused: 0,
    error: 0,
    waiting: 0,
    total: items.length,
  }
  for (const a of items) {
    counts[a.presentationStatus] += 1
  }
  return counts
}

export function useAgents(options: { enabled?: boolean } = {}): UseAgentsReturn {
  const { enabled = true } = options
  const [rawAgents, setRawAgents] = useState<AITradingAgent[]>(() => paperCache)
  const [liveById, setLiveById] = useState<Map<string, RealtimeAgentStatus>>(new Map())
  const [isLoading, setIsLoading] = useState(() => Boolean(enabled) && paperCache.length === 0)
  const [error, setError] = useState<string | null>(null)
  const [liveUnavailable, setLiveUnavailable] = useState(false)

  const refresh = useCallback(async () => {
    if (!enabled) {
      setRawAgents([])
      setIsLoading(false)
      setError(null)
      setLiveUnavailable(false)
      return
    }
    if (paperCache.length === 0) setIsLoading(true)
    try {
      const paperRes = await fetchPaperAgents()
      if (paperRes.agents.length > 0) {
        setRawAgents(paperRes.agents)
        setIsLoading(false)
        setError(null)
      }
      const liveRes = await fetchLiveAgents()
      const live = liveRes.ok ? liveRes.agents : []
      const paper = paperRes.agents
      const paperIds = new Set(paper.map((a) => a.id))
      const merged = [...paper, ...live.filter((a) => !paperIds.has(a.id))]
      if (merged.length > 0) {
        setRawAgents(merged)
      } else if (paperCache.length > 0) {
        setRawAgents(paperCache)
      }
      const liveDown = !liveRes.ok
      setLiveUnavailable(liveDown)
      if (merged.length === 0 && liveDown && !paperRes.ok) {
        setError(liveRes.message || 'Could not load agents')
      } else {
        setError(null)
      }
    } catch (err) {
      const paperRes = await fetchPaperAgents()
      if (paperRes.agents.length > 0) setRawAgents(paperRes.agents)
      else if (paperCache.length > 0) setRawAgents(paperCache)
      setLiveUnavailable(true)
      setError(
        paperRes.ok || paperCache.length > 0
          ? null
          : err instanceof Error
            ? err.message
            : 'Could not load agents'
      )
    } finally {
      setIsLoading(false)
    }
  }, [enabled])

  const upsertAgent = useCallback((agent: AITradingAgent) => {
    setRawAgents((prev) => {
      const idx = prev.findIndex((a) => a.id === agent.id)
      const next =
        idx === -1 ? [...prev, agent] : prev.map((row, i) => (i === idx ? { ...row, ...agent } : row))
      const paper = next.filter((a) => a.broker === 'paper' || a.model_id === 'paper')
      if (paper.length > 0) paperCache = paper
      return next
    })
  }, [])

  const applyRealtimeStatuses = useCallback((statuses: RealtimeAgentStatus[]) => {
    setLiveById(() => {
      const map = new Map<string, RealtimeAgentStatus>()
      for (const s of statuses) {
        map.set(s.agentId, s)
      }
      return map
    })
  }, [])

  useEffect(() => {
    const onPaper = (agents: AITradingAgent[]) => {
      if (agents.length === 0) return
      setRawAgents((prev) => {
        const ids = new Set(agents.map((a) => a.id))
        const live = prev.filter((a) => !ids.has(a.id) && a.broker !== 'paper' && a.model_id !== 'paper')
        return [...agents, ...live]
      })
      setIsLoading(false)
      setError(null)
    }
    paperListeners.add(onPaper)
    if (paperCache.length > 0) onPaper(paperCache)
    void refresh()
    return () => {
      paperListeners.delete(onPaper)
    }
  }, [refresh])

  const agents = useMemo(() => {
    return rawAgents.map((a) => restToListItem(a, liveById.get(a.id)))
  }, [rawAgents, liveById])

  const counts = useMemo(() => countStatuses(agents), [agents])

  return {
    agents,
    rawAgents,
    counts,
    isLoading,
    error,
    liveUnavailable,
    refresh,
    upsertAgent,
    applyRealtimeStatuses,
  }
}

if (typeof window !== 'undefined') {
  void fetchPaperAgents()
}

export default useAgents
